import { ReactElement } from 'react'
import { $getRoot, $isElementNode, DecoratorNode, DOMConversionMap, DOMConversionOutput, DOMExportOutput, EditorState, LexicalNode } from 'lexical'

import { CitationNodeComponent } from './CitationNodeComponent'

export class CitationNode extends DecoratorNode<ReactElement> {
  private readonly __id: string

  constructor(id: string, key?: string) {
    super(key)
    this.__id = id
  }

  static getType(): string {
    return 'superscript'
  }

  static clone(node: CitationNode): CitationNode {
    return new CitationNode(node.__id, node.__key)
  }

  getCitationId(): string {
    return this.__id
  }

  createDOM(): HTMLElement {
    return document.createElement('span')
  }

  updateDOM(): boolean {
    return false
  }

  static importDOM(): DOMConversionMap | null {
    return {
      sup: () => ({
        conversion: (domNode: HTMLElement): DOMConversionOutput => {
          const citationId = domNode.getAttribute('id')
          return {
            node: citationId !== null ? new CitationNode(citationId) : null,
          }
        },
        priority: 1,
      }),
    }
  }

  exportDOM(): DOMExportOutput {
    const sup = document.createElement('sup')
    sup.setAttribute('id', this.__id)
    sup.innerText = `[${this.__id}]`
    return {
      element: sup,
    }
  }

  static importJSON(serializedNode: any): CitationNode {
    return new CitationNode(serializedNode.id)
  }

  exportJSON(): any {
    return {
      type: CitationNode.getType(),
      version: 1,
      id: this.__id,
    }
  }

  decorate(): ReactElement {
    return <CitationNodeComponent key={this.__key} citationId={this.__id} />
  }
}

export function $createCitationNode(id: string): CitationNode {
  return new CitationNode(id)
}

export function $isCitationNode(node: LexicalNode | null | undefined): node is CitationNode {
  return node instanceof CitationNode
}

export const getCitationNodesFromEditorState = (editorState: EditorState) => {
  return editorState.read(() => {
    const root = $getRoot()
    return root.getChildren().reduce((nodes, node) => [...nodes, ...getCitationNodes(node)], [] as CitationNode[])
  })
}

const getCitationNodes = (node: LexicalNode): CitationNode[] => {
  if ($isCitationNode(node)) {
    return [node]
  }
  if ($isElementNode(node)) {
    return node.getChildren().reduce((nodes, childNode) => [...nodes, ...getCitationNodes(childNode)], [] as CitationNode[])
  }
  return []
}
